/**
 * Table scanning utilities for DOM Observer Context
 */

import type { TableContextData, TableInfo, TableRowData } from './types';

/**
 * Configuration constants for table scanning limits
 */
const MAX_SAMPLE_ROWS = 3;
const TABLE_STUB_THRESHOLD = 50000;

/**
 * Get table name from various sources (attributes, nearby headings, or path)
 */
export function getTableName(table: Element | null, pathname: string): string {
  let tableName =
    table?.getAttribute('aria-label') || table?.getAttribute('name') || table?.getAttribute('data-testid') || table?.id;

  // If still no table name, try to find a nearby heading
  if (!tableName) {
    // Use parentElement to avoid finding the table itself if it's a div
    const container = table?.parentElement?.closest('div, section, main');
    const heading = container?.querySelector('h1, h2, h3');
    if (heading?.textContent) {
      tableName = heading.textContent.trim();
    }
  }

  // If still no name, use the current page path
  if (!tableName) {
    const pathParts = pathname.split('/').filter(Boolean);
    tableName = pathParts[pathParts.length - 1] || 'unknown-table';
  }

  // Clean up the table name
  return tableName
    .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .toLowerCase();
}

/**
 * Extract column headers from a table
 */
export function extractTableHeaders(table: Element | null): string[] {
  const headerRow = table?.querySelector('[role="row"]');
  return headerRow
    ? Array.from(headerRow.querySelectorAll('[role="columnheader"]')).map(
        (header) => header.textContent?.trim() || 'unknown'
      )
    : [];
}

/**
 * Extract row data from selected table rows
 */
export function extractRowData(selectedRows: Element[], headers: string[]): TableRowData[] {
  return selectedRows.map((row) => {
    const cells = Array.from(row.querySelectorAll('[role="gridcell"], [role="cell"]'));
    const rowData: TableRowData = {};

    // Get row ID
    const id =
      row.getAttribute('data-id') ||
      row.getAttribute('data-rowindex') ||
      row.getAttribute('aria-rowindex') ||
      cells[0]?.textContent?.trim();

    rowData._id = id;

    // Map cells to headers
    cells.forEach((cell, index) => {
      const header = headers[index] || `column_${index}`;
      const text = cell.textContent?.trim();
      rowData[header] = text || '';
    });

    return rowData;
  });
}

/**
 * Deduplicate rows by ID (MUI DataGrid sometimes has duplicate elements)
 */
export function deduplicateRows(rows: TableRowData[]): TableRowData[] {
  return rows.filter((row, index, self) => index === self.findIndex((r) => r._id === row._id));
}

/**
 * Create table context data with optional stubbing for large datasets
 */
export function createTableContextData(
  tableName: string,
  headers: string[],
  uniqueRows: TableRowData[],
  stubThreshold = TABLE_STUB_THRESHOLD
): TableContextData {
  const dataJson = JSON.stringify(uniqueRows);
  const dataSize = dataJson.length;
  const shouldStub = dataSize > stubThreshold;

  if (shouldStub) {
    // Create a stub with summary info
    const selectedIds = uniqueRows.map((r) => r._id).filter(Boolean);
    return {
      tableName,
      selectedIds: selectedIds as string[],
      selectedCount: uniqueRows.length,
      sampleRows: uniqueRows.slice(0, MAX_SAMPLE_ROWS),
      columns: headers,
      _note: `Large selection (${uniqueRows.length} rows). Showing first ${MAX_SAMPLE_ROWS} as sample. Full data available via request_additional_context.`,
      _isStub: true,
    };
  }

  // Include full row data
  return {
    tableName,
    selectedCount: uniqueRows.length,
    columns: headers,
    rows: uniqueRows,
    _isStub: false,
  };
}

/**
 * Scan for table selections (MUI DataGrid adds specific aria attributes)
 */
export function scanTableSelections(pathname: string): TableContextData | null {
  // MUI DataGrid puts aria-selected="true" on selected rows
  // Exclude header rows by checking for columnheader role
  const allRows = document.querySelectorAll('[role="row"][aria-selected="true"]');
  const selectedRows = Array.from(allRows).filter(
    (row) =>
      // Make sure this is a data row, not a header row
      !row.querySelector('[role="columnheader"]')
  );

  if (selectedRows.length === 0) {
    return null;
  }

  // Try to find the table name from data attributes or nearby headers
  const table = selectedRows[0].closest('[role="grid"]');
  const tableName = getTableName(table, pathname);
  const headers = extractTableHeaders(table);
  const rowsData = extractRowData(selectedRows, headers);
  const uniqueRowsData = deduplicateRows(rowsData);

  if (uniqueRowsData.length === 0) {
    return null;
  }

  return createTableContextData(tableName, headers, uniqueRowsData);
}

/**
 * Scan for visible tables and their data
 */
export function scanVisibleTables(): TableInfo[] {
  const tables = document.querySelectorAll('[role="grid"], table');
  const tableData: TableInfo[] = [];

  tables.forEach((table, index) => {
    // Skip if not visible
    if ((table as HTMLElement).offsetParent === null) {
      return;
    }

    // Get table name
    const tableName =
      table.getAttribute('aria-label') || table.getAttribute('data-testid') || table.id || `table-${index + 1}`;

    // Get column headers
    const headers = Array.from(table.querySelectorAll('[role="columnheader"], th')).map(
      (header) => header.textContent?.trim() || ''
    );

    // Get row count (exclude header rows by filtering out rows that contain columnheader elements)
    const allRows = table.querySelectorAll('[role="row"], tbody tr');
    const rows = Array.from(allRows).filter((row) => !row.querySelector('[role="columnheader"]'));
    const rowCount = rows.length;

    // Get sample data
    const sampleData: any[] = [];
    Array.from(rows)
      .slice(0, MAX_SAMPLE_ROWS)
      .forEach((row) => {
        const cells = Array.from(row.querySelectorAll('[role="gridcell"], [role="cell"], td'));
        const rowData: Record<string, string> = {};
        cells.forEach((cell, i) => {
          const header = headers[i] || `col_${i}`;
          rowData[header] = cell.textContent?.trim() || '';
        });
        if (Object.keys(rowData).length > 0) {
          sampleData.push(rowData);
        }
      });

    if (headers.length > 0 || rowCount > 0) {
      tableData.push({
        name: tableName,
        rowCount,
        columnCount: headers.length,
        columns: headers,
        sampleData: sampleData.length > 0 ? sampleData : undefined,
      });
    }
  });

  return tableData;
}
